import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../utils/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { CurrencyInput } from "../components/ui/currency-input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { Plus, Search, ArrowLeftRight, Printer, Trash2 } from "lucide-react";
import { SearchableSelect } from "../components/ui/searchable-select";



const Offsets = () => {
  const [offsets, setOffsets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    from_type: "customer",  // Mahsup her zaman müşteriden başlar
    from_id: "",
    to_type: "supplier",    // ve tedarikçiye gider
    to_id: "",
    amount: "",
    notes: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [offsetsRes, custRes, suppRes] = await Promise.all([
        axios.get(`${API}/offsets`),
        axios.get(`${API}/customers/active/list`),  // Sadece aktif müşteriler
        axios.get(`${API}/suppliers`),
      ]);
      setOffsets(offsetsRes.data);
      setCustomers(custRes.data);
      setSuppliers(suppRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.from_id || !formData.to_id || formData.amount <= 0) {
      toast.error("Tüm zorunlu alanları doldurun");
      return;
    }

    try {
      await axios.post(`${API}/offsets`, formData);
      toast.success("Mahsup kaydedildi");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Failed to save:", error);
      toast.error("İşlem başarısız");
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split("T")[0],
      from_type: "customer",  // Mahsup her zaman müşteriden başlar
      from_id: "",
      to_type: "supplier",    // ve tedarikçiye gider
      to_id: "",
      amount: "",
      notes: "",
    });
  };

  const handleDelete = async (offsetId, documentNo) => {
    if (!window.confirm(`${documentNo} numaralı mahsup kaydını silmek istediğinizden emin misiniz?\n\nDikkat: Bu işlem bakiyeleri geri alacaktır.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/offsets/${offsetId}`);
      toast.success("Mahsup silindi");
      fetchData();
    } catch (error) {
      console.error("Failed to delete:", error);
      toast.error("Silme işlemi başarısız");
    }
  };

  const filteredOffsets = offsets.filter(
    (o) =>
      o.from_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.to_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.document_no.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6 print:space-y-4" data-testid="offsets-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Mahsup
          </h1>
          <p className="text-slate-500 mt-1">Cari hesaplar arası mahsup işlemleri</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handlePrint} className="no-print">
            <Printer className="h-4 w-4 mr-2" /> Yazdır
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-offset-btn">
                <Plus className="h-5 w-5 mr-2" />
                Yeni Mahsup
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Yeni Mahsup</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Tarih</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <div className="space-y-2">
                  <Label>Tahsilat Yapılan Müşteri</Label>
                  <p className="text-xs text-slate-500">Müşteriden tahsil edilecek tutar</p>
                  <SearchableSelect
                    options={customers.map((e) => ({
                      value: e.id,
                      label: e.name,
                      subtitle: e.phone || e.account_number,
                    }))}
                    value={formData.from_id}
                    onChange={(value) => setFormData({ ...formData, from_id: value })}
                    placeholder="Müşteri seçin"
                    searchPlaceholder="Müşteri ara..."
                    emptyMessage="Müşteri bulunamadı."
                    data-testid="offset-from-select"
                  />
                </div>

                <div className="flex justify-center items-center gap-2">
                  <ArrowLeftRight className="h-6 w-6 text-slate-400" />
                  <span className="text-sm text-slate-500">Mahsup</span>
                </div>

                <div className="space-y-2">
                  <Label>Ödeme Yapılan Tedarikçi</Label>
                  <p className="text-xs text-slate-500">Tedarikçiye ödenecek tutar</p>
                  <SearchableSelect
                    options={suppliers.map((e) => ({
                      value: e.id,
                      label: e.name,
                      subtitle: e.phone || e.account_number,
                    }))}
                    value={formData.to_id}
                    onChange={(value) => setFormData({ ...formData, to_id: value })}
                    placeholder="Tedarikçi seçin"
                    searchPlaceholder="Tedarikçi ara..."
                    emptyMessage="Tedarikçi bulunamadı."
                    data-testid="offset-to-select"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Tutar *</Label>
                <CurrencyInput
                  value={formData.amount}
                  onChange={(val) => setFormData({ ...formData, amount: val })}
                  placeholder="Tutar girin"
                  data-testid="offset-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Notlar</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Ek notlar"
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  İptal
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="offset-save-btn">
                  Kaydet
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Mahsup ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0 md:p-0">
          {filteredOffsets.length > 0 ? (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-slate-100">
                {filteredOffsets.map((offset) => (
                  <div key={offset.id} className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs font-mono text-slate-500">{offset.document_no}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-500">{offset.date}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(offset.id, offset.document_no)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700">
                        Müşteri
                      </span>
                      <span className="font-medium text-sm">{offset.from_name}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <ArrowLeftRight className="h-4 w-4 text-slate-400" />
                      <span className="px-2 py-0.5 rounded text-xs bg-amber-50 text-amber-700">
                        Tedarikçi
                      </span>
                      <span className="font-medium text-sm">{offset.to_name}</span>
                    </div>
                    <div className="flex items-center justify-end">
                      <span className="font-semibold tabular-nums">
                        ₺{offset.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Belge No</TableHead>
                      <TableHead className="font-semibold">Tarih</TableHead>
                      <TableHead className="font-semibold">Müşteri (Tahsilat)</TableHead>
                      <TableHead className="font-semibold">Tedarikçi (Ödeme)</TableHead>
                      <TableHead className="font-semibold text-right">Tutar</TableHead>
                      <TableHead className="font-semibold text-center w-20">İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOffsets.map((offset) => (
                      <TableRow key={offset.id}>
                        <TableCell className="font-mono text-sm">{offset.document_no}</TableCell>
                        <TableCell>{offset.date}</TableCell>
                        <TableCell>
                          <span className="px-2 py-1 rounded text-xs bg-blue-50 text-blue-700">
                            Müşteri
                          </span>
                          <span className="ml-2 font-medium">{offset.from_name}</span>
                        </TableCell>
                        <TableCell>
                          <span className="px-2 py-1 rounded text-xs bg-amber-50 text-amber-700">
                            Tedarikçi
                          </span>
                          <span className="ml-2 font-medium">{offset.to_name}</span>
                        </TableCell>
                        <TableCell className="text-right font-semibold tabular-nums">
                          ₺{offset.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(offset.id, offset.document_no)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <ArrowLeftRight className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">{searchTerm ? "Arama sonucu bulunamadı" : "Henüz mahsup yok"}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Offsets;
